import { logIfDevelopment } from '@lib/utils/logging';

import { ApiNames, ApiProxy, ApiRequest, HttpMethod } from '../../apiProxy';

const BASE_DISTR_URL = process.env.NEXT_PUBLIC_CF_PHOTOS_BASE_URL;

export class CastingProjectsQueryApi {
  private static apiProxy = ApiProxy.getInstance();
  private static apiName = ApiNames.CastingProjects;

  public static getAttachmentPresignedUrls(
    projectId: number,
    presignedUrlRequests: ProjectAttachmentPresignedUrlRequest[]
  ): Promise<ProjectAttachmentPresignedUrl[]> {
    const endpoint = `/projects/${projectId}/attachmentsPresignedUrls`;

    return this.apiProxy.execute(
      new ApiRequest(
        this.apiName,
        endpoint,
        HttpMethod.POST,
        presignedUrlRequests
      )
    );
  }

  public static createProjectAttachments(
    attachmentTypeId: number,
    createProjectAttachments: CreateProjectAttachment[]
  ): Promise<ProjectAttachment[]> {
    const endpoint = `/attachmentTypes/${attachmentTypeId}/attachments`;

    return this.apiProxy.execute(
      new ApiRequest(
        this.apiName,
        endpoint,
        HttpMethod.POST,
        createProjectAttachments
      )
    );
  }

  public static getProjectAttachmentTypes(
    projectId: number
  ): Promise<ProjectAttachmentTypeDetailed> {
    const endpoint = `/projects/${projectId}/attachmentTypes`;

    return this.apiProxy.execute(new ApiRequest(this.apiName, endpoint));
  }

  public static getProjectById(projectId: number): Promise<ProjectWithAddress> {
    const endpoint = `/projects/${projectId}`;

    return this.apiProxy.execute(new ApiRequest(this.apiName, endpoint));
  }

  public static getAllProjectsFromUser(
    userId: number
  ): Promise<ReducedProjectData[]> {
    const endpoint = `/casting/projects?userId=${userId}`;

    return this.apiProxy.execute(new ApiRequest(this.apiName, endpoint));
  }

  public static getProjectContacts(
    projectId: number
  ): Promise<ProjectContact[]> {
    const endpoint = `/projects/${projectId}/contacts`;

    return this.apiProxy.execute(new ApiRequest(this.apiName, endpoint));
  }

  public static getProjectBumps(projectId: number): Promise<ProjectBump[]> {
    const endpoint = `/projects/${projectId}/bumps`;

    return this.apiProxy.execute(new ApiRequest(this.apiName, endpoint));
  }

  public static getProjectUnits(projectId: number): Promise<ProjectUnit[]> {
    const endpoint = `/casting/projects/${projectId}/units`;

    return this.apiProxy.execute(
      new ApiRequest(ApiNames.ProjectLocations, endpoint)
    );
  }

  public static getProjectLocations(
    projectId: number
  ): Promise<ProjectLocation[]> {
    const endpoint = `/casting/projectLocations/${projectId}/all`;

    return this.apiProxy.execute(
      new ApiRequest(ApiNames.ProjectLocations, endpoint)
    );
  }

  public static getProjectNotes(
    projectId: number,
    projectDate: string
  ): Promise<ProjectNotes> {
    const endpoint = `/projects/${projectId}/notes?project_date=${projectDate}`;

    return this.apiProxy.execute(new ApiRequest(this.apiName, endpoint));
  }

  public static async getDefaultBumpAmounts(): Promise<DefaultBumpAmounts> {
    if (!BASE_DISTR_URL) {
      logIfDevelopment('BASE_DISTR_URL is not set');
      throw new Error('Something went wrong');
    }

    const endpoint = `${BASE_DISTR_URL}/common-files/timesheets-default-bumps-values-v2.json`;
    const response = await fetch(endpoint);

    if (!response.ok) {
      logIfDevelopment('Failed to fetch default bump amounts');
      throw new Error('Something went wrong');
    }

    return await response.json();
  }

  public static getProjectQuestionSuggestions(
    projectId: number,
    suggestionType: QuestionSuggestionType | null = null
  ): Promise<QuestionSuggestion[]> {
    const queryParams = () => {
      if (!suggestionType) return '';

      return `?suggestionType=${suggestionType}`;
    };

    const endpoint = `/projects/${projectId}/suggestedQuestions${queryParams()}`;

    return this.apiProxy.execute(new ApiRequest(this.apiName, endpoint));
  }

  // wardrobe / hmu
  public static getProjectWardrobeHmu(
    projectId: number,
    wardrobeOrHmu: WardrobeOrHmu
  ): Promise<ProjectWardrobe[]>;
  public static getProjectWardrobeHmu(
    projectId: number,
    wardrobeOrHmu: 'hmu'
  ): Promise<ProjectHmu[]>;
  public static getProjectWardrobeHmu<T extends ProjectWardrobe | ProjectHmu>(
    projectId: number,
    wardrobeOrHmu: WardrobeOrHmu
  ): Promise<T[]> {
    const endpoint = `/projects/${projectId}/wardrobeHmu?resourceType=${wardrobeOrHmu}`;

    return this.apiProxy.execute(new ApiRequest(this.apiName, endpoint));
  }

  public static async getCastingProductionProjectLinks(
    projectId: number
  ): Promise<PayrollCastingLink> {
    const endpoint = `/payrollCastingLink/${projectId}`;

    return this.apiProxy.execute(
      new ApiRequest(ApiNames.PayrollToCasting, endpoint)
    );
  }

  public static async getSyncBookingsHistory(
    unitIds: number[]
  ): Promise<SyncBookingsHistory> {
    const endpoint = `/projects/syncBookingsHistory?unit_ids=[${unitIds}]`;

    return this.apiProxy.execute(new ApiRequest(this.apiName, endpoint));
  }

  // public endpoint
  public static getClientEnvironment(
    clientEnvironmentId: number
  ): Promise<ClientEnvironment> {
    const endpoint = `/clientEnvironment/${clientEnvironmentId}`;

    return this.apiProxy.execute(
      new ApiRequest(this.apiName, endpoint, HttpMethod.GET, {}, {}, {}, true)
    );
  }

  public static getCastingUsers(
    include_hidden_users_from_settings = false
  ): Promise<CastingUser[]> {
    let endpoint = `/castingUsers`;

    if (include_hidden_users_from_settings) {
      endpoint += `?include_hidden_users_from_settings=true`;
    }

    return this.apiProxy.execute(
      new ApiRequest(this.apiName, endpoint, HttpMethod.GET)
    );
  }

  public static getIsCastingSignupKeyExpired(
    signupKey: string
  ): Promise<{ is_expired: boolean }> {
    const endpoint = `/castingUserSignupKey/getIsExpired`;

    return this.apiProxy.execute(
      new ApiRequest(
        this.apiName,
        endpoint,
        HttpMethod.POST,
        {
          signup_key: signupKey,
        },
        {},
        {},
        true
      )
    );
  }

  public static getCastingUsersWithIndividualAccessToProject(
    projectId: number
  ): Promise<CastingUserWithProjectAccess[]> {
    const endpoint = `/projects/${projectId}/usersWithIndividualAccess`;

    return this.apiProxy.execute(
      new ApiRequest(this.apiName, endpoint, HttpMethod.GET)
    );
  }
}
